import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Clock,
  Mail,
  MapPin,
  Phone,
  Plus,
  Search,
  User as UserIcon,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Drawer } from "../../components/ui/Drawer";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Table, THead, TBody, TR, TH, TD } from "../../components/ui/Table";
import { Skeleton } from "../../components/ui/Skeleton";
import { KV } from "../../components/ui/KV";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatDate } from "../../utils/formatters";
import { getHoldingCompanies } from "../../api/holding";

const LIMIT = 20;

export default function HoldingCompaniesList() {
  const { t } = useTranslation();
  const [data, setData] = useState({ docs: [], totalDocs: 0, page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Búsqueda libre (server-side) por nombre, razón social o RUT. `queryInput`
  // es lo que está tipeando el usuario; `query` es el valor con debounce que
  // dispara el fetch.
  const [queryInput, setQueryInput] = useState("");
  const query = useDebouncedValue(queryInput, 300);

  // Al cambiar la búsqueda, resetea la paginación a 1 para que el usuario
  // vea los resultados desde el inicio.
  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getHoldingCompanies({ page, limit: LIMIT, q: query })
      .then((res) => {
        if (!active) return;
        if (res?.error) {
          setError(res.error);
          return;
        }
        setData(res);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [page, query]);

  const companies = data?.docs || [];
  const totalDocs = data?.totalDocs ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="w-full min-w-0 p-6 space-y-5">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">{t("Empresas hijas")}</h1>
          <p className="text-sm text-fg-muted mt-1">
            {query
              ? `${totalDocs.toLocaleString()} ${totalDocs === 1 ? t("resultado") : t("resultados")} ${t('para')} "${query}"`
              : `${totalDocs.toLocaleString()} ${totalDocs === 1 ? t("empresa registrada") : t("empresas registradas")} ${t("en el holding.")}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-fg-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder={t("Buscar por nombre, razón social o RUT...")}
              aria-label={t("Buscar empresa")}
              className="h-10 w-full sm:w-80 rounded-lg border border-border bg-bg pl-9 pr-9 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            {queryInput && (
              <button
                type="button"
                onClick={() => setQueryInput("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-fg-muted hover:text-fg transition-colors"
                aria-label={t("Limpiar búsqueda")}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Link
            to="/holding/companies/new"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 active:bg-brand-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t("Nueva empresa")}
          </Link>
        </div>
      </header>

      <div className="hidden md:block rounded-lg border border-border overflow-hidden">
        <Table>
          <THead>
            <TR>
              <TH>{t("Empresa")}</TH>
              <TH>{t("Identificador")}</TH>
              <TH>{t("Puntos de retiro")}</TH>
              <TH>{t("Horario")}</TH>
              <TH>{t("País")}</TH>
              <TH></TH>
            </TR>
          </THead>
          <TBody>
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TR key={`sk-${i}`}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TD key={j}>
                      <Skeleton className="h-4 w-full max-w-[140px]" />
                    </TD>
                  ))}
                </TR>
              ))}
            {!loading && companies.length === 0 && (
              <TR>
                <TD colSpan={6} className="!p-0">
                  {error ? (
                    <EmptyState
                      icon={<Building2 />}
                      title={t("No pudimos cargar las empresas")}
                      description={t("Reintentá en unos segundos. Si persiste, contactá a soporte.")}
                      action={
                        <Button variant="outline" onClick={() => setPage((p) => p)}>
                          {t("Reintentar")}
                        </Button>
                      }
                    />
                  ) : query ? (
                    <EmptyState
                      icon={<Search />}
                      title={`${t("Sin resultados para")} "${query}"`}
                      description={t("Probá con otro nombre, razón social o RUT.")}
                      action={
                        <Button variant="outline" onClick={() => setQueryInput("")}>
                          {t("Limpiar búsqueda")}
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<Building2 />}
                      title={t("Sin empresas hijas todavía")}
                      description={t("Comenzá creando la primera empresa de tu holding.")}
                      action={
                        <Link
                          to="/holding/companies/new"
                          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          {t("Nueva empresa")}
                        </Link>
                      }
                    />
                  )}
                </TD>
              </TR>
            )}
            {!loading &&
              companies.map((c) => {
                const addrs = c.shippingAddresses || [];
                const firstAddr = addrs[0];
                const restCount = Math.max(0, addrs.length - 1);
                return (
                  <TR key={c._id} className="cursor-pointer" onClick={() => setSelected(c)}>
                    <TD>
                      <div className="font-medium text-fg">{c.name || "—"}</div>
                      {c.legalName && c.legalName !== c.name && (
                        <div className="text-xs text-fg-muted">{c.legalName}</div>
                      )}
                    </TD>
                    <TD>
                      <div className="font-mono text-sm">{c.businessIdentifier || "—"}</div>
                      {c.businessIdentifierType && (
                        <div className="text-[11px] text-fg-muted uppercase tracking-wider">
                          {c.businessIdentifierType}
                        </div>
                      )}
                    </TD>
                    <TD>
                      {firstAddr ? (
                        <div className="flex items-center gap-2">
                          <div className="min-w-0">
                            <div className="text-sm text-fg truncate max-w-[220px]">
                              {firstAddr.municipality || "—"}
                            </div>
                            <div className="text-xs text-fg-muted truncate max-w-[220px]">
                              {firstAddr.address || ""}
                            </div>
                          </div>
                          {restCount > 0 && (
                            <Badge tone="neutral" className="shrink-0">
                              +{restCount}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-fg-muted text-sm">—</span>
                      )}
                    </TD>
                    <TD>
                      {c.pickUpTime ? (
                        <span className="inline-flex items-center gap-1.5 text-sm">
                          <Clock className="w-3.5 h-3.5 text-fg-muted" />
                          {c.pickUpTime}
                        </span>
                      ) : (
                        <span className="text-fg-muted text-sm">—</span>
                      )}
                    </TD>
                    <TD className="text-sm">{c.operationsCountry || "—"}</TD>
                    <TD className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Link
                        to={`/holding/companies/${c._id}`}
                        className="text-brand-700 hover:underline text-sm font-medium"
                      >
                        {t("Editar")}
                      </Link>
                    </TD>
                  </TR>
                );
              })}
          </TBody>
        </Table>
      </div>

      <div className="md:hidden space-y-2">
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={`m-sk-${i}`} className="rounded-lg border border-border bg-bg p-3 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        {!loading && companies.length === 0 && (
          error ? (
            <EmptyState
              icon={<Building2 />}
              title={t("No pudimos cargar las empresas")}
              description={t("Reintentá en unos segundos.")}
              action={
                <Button variant="outline" onClick={() => setPage((p) => p)}>
                  {t("Reintentar")}
                </Button>
              }
            />
          ) : query ? (
            <EmptyState
              icon={<Search />}
              title={`${t("Sin resultados para")} "${query}"`}
              description={t("Probá con otro nombre, razón social o RUT.")}
              action={
                <Button variant="outline" onClick={() => setQueryInput("")}>
                  {t("Limpiar búsqueda")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<Building2 />}
              title={t("Sin empresas hijas todavía")}
              description={t("Crea la primera empresa de tu holding.")}
            />
          )
        )}
        {!loading &&
          companies.map((c) => {
            const addrs = c.shippingAddresses || [];
            const firstAddr = addrs[0];
            const restCount = Math.max(0, addrs.length - 1);
            return (
              <button
                key={c._id}
                type="button"
                onClick={() => setSelected(c)}
                className="w-full text-left rounded-lg border border-border bg-bg p-3 space-y-1 hover:bg-bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-fg truncate">{c.name || "—"}</span>
                  {addrs.length > 0 && (
                    <Badge tone="neutral" className="shrink-0">
                      {addrs.length} {addrs.length === 1 ? t("punto") : t("puntos")}
                    </Badge>
                  )}
                </div>
                {c.businessIdentifier && (
                  <div className="font-mono text-xs text-fg-muted">{c.businessIdentifier}</div>
                )}
                {firstAddr && (
                  <div className="text-xs text-fg-muted flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {firstAddr.municipality}
                    {restCount > 0 ? ` + ${restCount} ${t("más")}` : ""}
                  </div>
                )}
                {c.pickUpTime && (
                  <div className="text-xs text-fg-muted flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t("Retiro a las")} {c.pickUpTime}
                  </div>
                )}
              </button>
            );
          })}
      </div>

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border bg-bg text-fg hover:bg-bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={t("Página anterior")}
          >
            ‹
          </button>
          <span className="text-sm text-fg-muted tabular-nums">
            {t("Página")} {data?.page ?? page} {t("de")} {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border bg-bg text-fg hover:bg-bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={t("Página siguiente")}
          >
            ›
          </button>
        </div>
      )}

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected ? selected.name || t("Empresa") : ""}
        description={
          selected
            ? [
                selected.legalName,
                selected.businessIdentifier,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
        footer={
          selected ? (
            <>
              <Button variant="ghost" onClick={() => setSelected(null)}>
                {t("Cerrar")}
              </Button>
              <Link
                to={`/holding/companies/${selected._id}`}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 active:bg-brand-700 transition-colors"
              >
                {t("Editar empresa")}
              </Link>
            </>
          ) : null
        }
      >
        {selected && (
          <div className="space-y-5">
            <Section title={t("Identificación")}>
              <KV label={t("Razón social")} value={selected.legalName} />
              <KV
                label={selected.businessIdentifierType || t("Identificador")}
                value={selected.businessIdentifier}
                mono
              />
              <KV label={t("Dirección fiscal")} value={selected.fiscalAddress} />
              <KV label={t("País")} value={selected.operationsCountry} />
              {selected.pickUpTime && (
                <KV label={t("Horario de retiro")} value={selected.pickUpTime} />
              )}
              {selected.createdAt && (
                <KV label={t("Creada el")} value={formatDate(selected.createdAt)} />
              )}
            </Section>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-fg-muted" />
                <h3 className="text-sm font-semibold text-fg">
                  {t("Puntos de retiro")}
                  {(selected.shippingAddresses?.length ?? 0) > 0 && (
                    <span className="ml-2 text-xs font-normal text-fg-muted">
                      ({selected.shippingAddresses.length})
                    </span>
                  )}
                </h3>
              </div>
              {(selected.shippingAddresses || []).length === 0 ? (
                <div className="rounded-lg border border-border bg-bg-muted/40 p-3 text-sm text-fg-muted">
                  {t("No hay puntos de retiro registrados para esta empresa.")}
                </div>
              ) : (
                <ul className="space-y-2">
                  {selected.shippingAddresses.map((a, idx) => (
                    <li
                      key={a._id || idx}
                      className="rounded-lg border border-border bg-bg-muted/40 p-3 space-y-1"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-medium text-fg">{a.address || "—"}</div>
                        {a.type && (
                          <Badge tone="neutral" className="shrink-0">
                            {a.type}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-fg-muted">
                        {[a.municipality, a.state].filter(Boolean).join(", ") || "—"}
                      </div>
                      {a.contactPhone && (
                        <div className="text-xs text-fg-muted flex items-center gap-1.5">
                          <Phone className="w-3 h-3" />
                          <a href={`tel:${a.contactPhone}`} className="hover:underline">
                            {a.contactPhone}
                          </a>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-fg mb-2">{t("Contactos")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ContactsBlock
                  title={t("Operaciones")}
                  items={selected.contacts?.operationsContacts}
                />
                <ContactsBlock
                  title={t("Facturación y cobranza")}
                  items={selected.contacts?.billingContacts}
                />
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-fg mb-2">{title}</h3>
      <div className="rounded-lg border border-border bg-bg-muted/40 p-3 space-y-1">
        {children}
      </div>
    </div>
  );
}

function ContactsBlock({ title, items }) {
  const { t } = useTranslation();
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="rounded-lg border border-border bg-bg-muted/40 p-3">
      <div className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider mb-2">
        {title}
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-fg-muted">{t("Sin contactos registrados.")}</div>
      ) : (
        <ul className="space-y-3">
          {list.map((p, idx) => (
            <li key={p.email || p.phone || p.rut || idx} className="text-sm space-y-0.5">
              <div className="font-medium text-fg flex items-center gap-1.5">
                <UserIcon className="w-3.5 h-3.5 text-fg-muted" />
                {p.name || "—"}
              </div>
              {p.role && (
                <div className="text-[11px] text-fg-muted uppercase tracking-wider">
                  {p.role}
                </div>
              )}
              {p.email && (
                <div className="text-xs text-fg-muted flex items-center gap-1.5">
                  <Mail className="w-3 h-3" />
                  <a href={`mailto:${p.email}`} className="hover:underline truncate">
                    {p.email}
                  </a>
                </div>
              )}
              {p.phone && (
                <div className="text-xs text-fg-muted flex items-center gap-1.5">
                  <Phone className="w-3 h-3" />
                  <a href={`tel:${p.phone}`} className="hover:underline">
                    {p.phone}
                  </a>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
